import {
  SyncQuery,
  SyncRequestsOrderBy,
  SyncRunsCountQuery,
  SyncRunsQuery,
  useSyncRunsCountQuery,
  useSyncRunsQuery,
} from "src/graphql";

import { FC, useEffect, useMemo, useState } from "react";
import {
  ArrowRightIcon,
  Badge,
  Box,
  CloseIcon,
  Popover,
  PopoverBody,
  PopoverContent,
  ChevronDownIcon,
  Column,
  FilterMenu,
  FilterMenuButton,
  FilterMenuGroup,
  FilterMenuList,
  FilterMenuOption,
  IconButton,
  NumberInput,
  PlayIcon,
  Row,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@hightouch/htui";
import { Link, useNavigate } from "src/router";
import { useSyncOutletContext } from "./outlet";
import { SyncRequestErrorInfo } from "src/types/sync-errors";
import {
  BasicPagination,
  Table,
  TableColumn,
  useTableConfig,
} from "src/ui/table";
import { commaNumber } from "src/utils/numbers";
import {
  getSyncRunOperations,
  isDeprecatedSyncRunError,
  isSyncRunStatus,
  SyncRunStatus,
  UnknownSyncRunStatus,
} from "src/utils/syncs";
import { openUrl } from "src/utils/urls";
import { QueryType } from "src/types/models";

import { SyncRequestErrorModal } from "./components/error-modals";
import { isSyncMatchBoosted } from "./matchbooster";
import { SyncRunStatusIndicator } from "src/components/syncs/sync-run-status-indicator";
import { ElementOf } from "ts-essentials";
import { useDraft } from "src/contexts/draft-context";
import { PermissionedButton } from "src/components/permission";
import { RunOperationsColumn } from "./components/run-operations-column";
import {
  formatRunCreatedAt,
  getSyncRunResyncLabel,
  syncRunStatusDurationText,
} from "./components/utils";
import { TextWithTooltip } from "src/components/text-with-tooltip";
import { useEntitlements } from "src/hooks/use-entitlement";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { useCurrentRun } from "./utils/use-current-run";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PopoverTriggerButton } from "src/ui/popover";
import {
  formatDateTimeLocal,
  RowResultMetric,
  SyncRunsFilter,
  ThresholdComparator,
} from "./utils/runs-filter";
import { useSyncRunsFilters } from "src/pages/syncs/sync/utils/use-sync-runs-filters";

const COMPARATOR_OPTIONS: { label: string; value: ThresholdComparator }[] = [
  { label: "Any", value: "any" },
  { label: "Over", value: "over" },
  { label: "Under", value: "under" },
];

const ROW_RESULT_METRIC_OPTIONS: { label: string; value: RowResultMetric }[] = [
  { label: "Rejected rows", value: "rejected" },
  { label: "Successful rows", value: "successful" },
];

enum SortKeys {
  RowsQueried = "query_run.size",
  CreatedAt = "created_at",
}

type Props = {
  onRun: () => void;
  sync: SyncQuery["syncs"][0];
  redirectPrefix?: string;
};

const PAGE_SIZE = 10;

export const Runs: FC = () => {
  const { sync } = useSyncOutletContext();
  const { startRun } = useCurrentRun(sync.id);

  return (
    <SyncRuns
      sync={sync}
      onRun={() => {
        startRun();
      }}
    />
  );
};

export const SyncRuns: FC<Readonly<Props>> = ({
  sync,
  onRun,
  redirectPrefix = "",
}) => {
  const { editingDraft } = useDraft();
  const {
    appSyncRunSummaryPageEnabled,
    appShowDebugSuggestionOnly,
    appSyncRunsPageInputEnabled,
    appSyncRunsFiltersEnabled,
  } = useFlags();
  const { data: entitlementsData } = useEntitlements(true);
  const overageLockout: boolean = entitlementsData.overage?.overageLockout;
  const overageText = entitlementsData.overage?.destinationOverageText;

  const navigate = useNavigate();
  const [runError, setError] = useState<
    SyncRequestErrorInfo & { syncStatus: SyncRunStatus }
  >();

  const filtersEnabled = Boolean(appSyncRunsFiltersEnabled);

  const {
    runsFilter,
    rangeStart,
    rangeEnd,
    operationsComparator,
    operationsThreshold,
    rowResultMetric,
    rowResultComparator,
    rowResultThreshold,
    setRunsFilter,
    setRangeStart,
    setRangeEnd,
    setOperationsComparator,
    setOperationsThreshold,
    setRowResultMetric,
    setRowResultComparator,
    setRowResultThreshold,
    clearFilters,
    hasActiveFilters,
    hasuraFilter,
    skipRunsCount,
  } = useSyncRunsFilters({ syncId: sync.id, enabled: filtersEnabled });

  const isJourneyTriggered = sync.segment?.query_type === QueryType.JourneyNode;

  const { limit, offset, orderBy, page, setPage, onSort } =
    useTableConfig<SyncRequestsOrderBy>({
      defaultSortKey: "created_at",
      sortOptions: Object.values(SortKeys),
      limit: PAGE_SIZE,
    });

  const { refetchInterval } = useGovernedRefetchInterval<SyncRunsQuery>({
    baseMs: 5000,
    compareKey: (data) =>
      data?.sync_requests?.map((r) => [
        r.id,
        r.status,
        r.finished_at,
        r.sync_attempts?.[0]?.status,
      ]),
  });

  const {
    data,
    error,
    isLoading: isLoading,
    isPreviousData,
  } = useSyncRunsQuery(
    {
      filter: hasuraFilter,
      offset,
      limit: limit + 1, // use limit + 1 to look ahead if another page is available
      orderBy,
    },
    {
      refetchInterval,
      notifyOnChangeProps: "tracked",
      keepPreviousData: true,
    },
  );

  // Polled separately (and slower) from the runs list so the 5s poll doesn't
  // pay for a count(*) on every tick.
  const { refetchInterval: countRefetchInterval } =
    useGovernedRefetchInterval<SyncRunsCountQuery>({
      baseMs: 30000,
      compareKey: (data) => [data?.sync_requests_aggregate?.aggregate?.count],
    });

  // `skipRunsCount` means a computed-field predicate is active; the page input
  // then renders without "of N" rather than paying for a full-history scan.
  const { data: countData } = useSyncRunsCountQuery(
    { filter: hasuraFilter },
    {
      enabled: Boolean(appSyncRunsPageInputEnabled) && !skipRunsCount,
      refetchInterval: countRefetchInterval,
    },
  );

  const totalRuns = skipRunsCount
    ? undefined
    : countData?.sync_requests_aggregate?.aggregate?.count;
  const totalPages =
    totalRuns === undefined
      ? undefined
      : Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));

  const runs = useMemo(() => {
    const runs = data?.sync_requests;
    if (runs?.length === PAGE_SIZE + 1) {
      return runs.slice(0, PAGE_SIZE);
    }
    return runs;
  }, [data?.sync_requests]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    // reset pagination when any filter changes
    setPage(0);
  }, [hasuraFilter]);

  const showPendingRows =
    isSyncMatchBoosted(sync?.config) ||
    runs?.some(
      (run) => run?.query_run?.pending_rows && run.query_run.pending_rows > 0,
    );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const columns = useMemo(
    (): TableColumn<ElementOf<SyncRunsQuery["sync_requests"]>>[] => [
      {
        name: "Started",
        min: "min-content",
        sortDirection: orderBy?.created_at,
        onClick: () => onSort(SortKeys.CreatedAt),
        cell: ({ created_at }) => {
          const date = formatRunCreatedAt(created_at);
          return (
            <TextWithTooltip message={date ?? ""}>
              {date ?? "--"}
            </TextWithTooltip>
          );
        },
      },
      {
        name: "Run status",
        min: "min-content",
        cell: ({
          status_computed,
          completion_ratio,
          created_at: createdAt,
          sync_attempts,
          error: syncRequestError,
          error_code_detail,
          diff_mode_override,
          resync_reason,
          finished_at,
        }) => {
          if (!isSyncRunStatus(status_computed)) {
            return (
              <SyncRunStatusIndicator
                status={UnknownSyncRunStatus}
                completionRatio={null}
              />
            );
          }
          const attempt = sync_attempts?.[0];

          // Use `sync_requests.error` if present, then fall back to `sync_attempts.error`.
          const error: SyncRequestErrorInfo =
            syncRequestError ||
            (attempt?.error && !isDeprecatedSyncRunError(attempt?.error)
              ? { message: attempt?.error }
              : undefined);

          if (error && error_code_detail)
            error.errorCodeDetail = error_code_detail;

          const syncRunFailed = status_computed === SyncRunStatus.FAILED;

          const resyncLabel = getSyncRunResyncLabel({
            diffModeOverride: diff_mode_override,
            resyncReason: resync_reason,
          });

          return (
            <Column fontWeight="medium">
              <Row gap={1} alignItems="center">
                <SyncRunStatusIndicator
                  status={status_computed}
                  completionRatio={completion_ratio}
                />
                <Text color="text.secondary" fontWeight="normal">
                  {syncRunStatusDurationText(
                    status_computed,
                    createdAt,
                    finished_at,
                  )}
                </Text>
                {resyncLabel && <Badge size="sm">{resyncLabel}</Badge>}
              </Row>
              <Row>
                {syncRunFailed && (
                  <Link
                    href=""
                    ml={6}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      setError({
                        ...(error ?? {}),
                        syncStatus: status_computed,
                      });
                    }}
                  >
                    <Text size="sm" color="danger.base">
                      See error message
                      <ArrowRightIcon ml={1} />
                    </Text>
                  </Link>
                )}
              </Row>
            </Column>
          );
        },
      },
      {
        sortDirection: orderBy?.query_run?.size,
        onClick: () => onSort(SortKeys.RowsQueried),
        header: () => (
          <Row align="center" justify="flex-end" w="100%" pos="relative">
            <Tooltip message="Number of rows in your full model query results">
              <Text whiteSpace="nowrap" size="sm" color="text.secondary">
                Rows queried
              </Text>
            </Tooltip>
          </Row>
        ),
        cell: ({ query_run }) => {
          return (
            <Column align="flex-end" flex={1}>
              <Text>
                {query_run?.size !== undefined
                  ? commaNumber(query_run.size)
                  : "--"}
              </Text>
            </Column>
          );
        },
      },
      {
        header: () => (
          <Row align="center" justify="flex-end" w="100%" pos="relative">
            <Tooltip message="Number of operations performed during each sync run">
              <Text size="sm" color="text.secondary">
                Operations
              </Text>
            </Tooltip>
          </Row>
        ),
        cell: ({
          planner_type,
          computed_progress,
          add_executed,
          change_executed,
          remove_executed,
          query_run,
          sync_attempts,
          status_computed,
        }) => {
          const { successful, rejected } = getSyncRunOperations({
            attempt: sync_attempts?.[0],
            syncRequest: {
              status_computed,
              error,
              planner_type,
              add_executed,
              change_executed,
              remove_executed,
              computed_progress,
            },
            queryRun: query_run,
          });

          const added = successful.added + rejected.added;
          const changed = successful.changed + rejected.changed;
          const removed = successful.removed + rejected.removed;

          const total = added + changed + removed;

          if (total === 0) {
            return (
              <Column align="flex-end" flex={1}>
                <Text>--</Text>
              </Column>
            );
          }

          return (
            <Column align="flex-end" flex={1}>
              <Popover placement="bottom-end" offset={[0, -14]}>
                <PopoverContent>
                  <PopoverBody
                    p={0}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Row
                      justify="space-between"
                      w="100%"
                      p={3}
                      borderBottomWidth="hairline"
                      borderBottomStyle="solid"
                      borderColor="base.border"
                    >
                      <Text color="text.secondary" mr={6} fontWeight="medium">
                        Add row
                      </Text>
                      <Text>{commaNumber(added)}</Text>
                    </Row>
                    <Row
                      justify="space-between"
                      w="100%"
                      p={3}
                      borderBottomWidth="hairline"
                      borderBottomStyle="solid"
                      borderColor="base.border"
                    >
                      <Text color="text.secondary" mr={6} fontWeight="medium">
                        Change row
                      </Text>
                      <Text>{commaNumber(changed)}</Text>
                    </Row>
                    <Row justify="space-between" w="100%" p={3}>
                      <Text color="text.secondary" mr={6} fontWeight="medium">
                        Remove row
                      </Text>
                      <Text>{commaNumber(removed)}</Text>
                    </Row>
                  </PopoverBody>
                </PopoverContent>
                <PopoverTriggerButton
                  aria-label="View operations"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Row justify="flex-end" align="center">
                    <Text>{commaNumber(total)}</Text>
                    <Row fontSize="20px">
                      <ChevronDownIcon />
                    </Row>
                  </Row>
                </PopoverTriggerButton>
              </Popover>
            </Column>
          );
        },
      },
      {
        header: () => (
          <Row align="center" justifyContent="center" w="100%" pos="relative">
            <Tooltip message="Breakdown of operations that were successful or rejected during each sync run">
              <Text size="sm" color="text.secondary">
                Results
              </Text>
            </Tooltip>
          </Row>
        ),
        cellSx: {
          justifyContent: "right",
        },
        cell: ({
          error,
          planner_type,
          query_run,
          sync_attempts,
          computed_progress,
          add_executed,
          change_executed,
          remove_executed,
          status_computed,
        }) => (
          <RunOperationsColumn
            showPendingRows={Boolean(showPendingRows)}
            queryRun={query_run}
            syncAttempts={sync_attempts}
            syncRequest={{
              status_computed,
              error,
              planner_type,
              computed_progress,
              add_executed,
              change_executed,
              remove_executed,
            }}
          />
        ),
      },
      {
        max: "100px",
        cell: ({ id }) => {
          return (
            <Column align="flex-end" flex={1}>
              <PermissionedButton
                permission={
                  appSyncRunSummaryPageEnabled || appShowDebugSuggestionOnly
                    ? undefined
                    : {
                        v2: {
                          resource: "sync",
                          grant: "can_debug",
                          id: sync.id,
                        },
                      }
                }
                size="sm"
                variant="secondary"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  const url = `${redirectPrefix}/syncs/${sync.id}/runs/${id}${
                    editingDraft ? "?editing=true" : ""
                  }`;

                  openUrl(url, navigate, event);
                }}
              >
                View run
              </PermissionedButton>
            </Column>
          );
        },
      },
    ],
    [orderBy],
  );

  return (
    <>
      <Column align="start">
        {runs?.[0]?.planner_type === "all" ||
        (!hasActiveFilters && !runs?.length) ? null : (
          <Row p={6} gap={4} align="center" flexWrap="wrap">
            {!filtersEnabled && (
              <FilterMenu>
                <FilterMenuButton>Filter</FilterMenuButton>

                <FilterMenuList>
                  <FilterMenuGroup
                    title="View"
                    type="radio"
                    value={runsFilter}
                    onChange={(val) => setRunsFilter(val as SyncRunsFilter)}
                  >
                    <FilterMenuOption value={SyncRunsFilter.ALL_RUNS}>
                      Show all runs
                    </FilterMenuOption>
                    <FilterMenuOption value={SyncRunsFilter.NO_OPERATIONS}>
                      Hide runs with 0 operations
                    </FilterMenuOption>
                    <FilterMenuOption
                      value={SyncRunsFilter.NO_SUCCESSFUL_OPERATIONS}
                    >
                      Hide runs with 0 successful operations
                    </FilterMenuOption>
                  </FilterMenuGroup>
                </FilterMenuList>
              </FilterMenu>
            )}

            {filtersEnabled && (
              <>
                <Row align="center" gap={2}>
                  <Text color="text.secondary" whiteSpace="nowrap">
                    Started
                  </Text>
                  <TextInput
                    aria-label="Started after"
                    type="datetime-local"
                    size="sm"
                    width="auto"
                    max={formatDateTimeLocal(rangeEnd)}
                    value={formatDateTimeLocal(rangeStart) ?? ""}
                    onChange={(event) => {
                      const { value } = event.target;
                      setRangeStart(value ? new Date(value) : undefined);
                    }}
                  />
                  <Text color="text.secondary">–</Text>
                  <TextInput
                    aria-label="Started before"
                    type="datetime-local"
                    size="sm"
                    width="auto"
                    min={formatDateTimeLocal(rangeStart)}
                    value={formatDateTimeLocal(rangeEnd) ?? ""}
                    onChange={(event) => {
                      const { value } = event.target;
                      setRangeEnd(value ? new Date(value) : undefined);
                    }}
                  />
                </Row>

                <Row align="center" gap={2}>
                  <Text color="text.secondary" whiteSpace="nowrap">
                    Operations
                  </Text>
                  <Select
                    aria-label="Operations comparator"
                    size="sm"
                    width="auto"
                    options={COMPARATOR_OPTIONS}
                    value={operationsComparator}
                    onChange={(value) => {
                      if (value !== undefined) {
                        setOperationsComparator(value);
                      }
                    }}
                  />
                  {operationsComparator !== "any" && (
                    <Box width={28}>
                      <NumberInput
                        aria-label="Operations threshold"
                        size="sm"
                        width="100%"
                        min={0}
                        // An operation count is a whole number, and without
                        // this the default allows 6 fraction digits.
                        formatOptions={{ maximumFractionDigits: 0 }}
                        placeholder="Count"
                        value={operationsThreshold}
                        onChange={setOperationsThreshold}
                      />
                    </Box>
                  )}
                </Row>

                <Row align="center" gap={2}>
                  <Select
                    aria-label="Row result metric"
                    size="sm"
                    width="auto"
                    options={ROW_RESULT_METRIC_OPTIONS}
                    value={rowResultMetric}
                    onChange={(value) => {
                      if (value !== undefined) {
                        setRowResultMetric(value);
                      }
                    }}
                  />
                  <Select
                    aria-label="Row result comparator"
                    size="sm"
                    width="auto"
                    options={COMPARATOR_OPTIONS}
                    value={rowResultComparator}
                    onChange={(value) => {
                      if (value !== undefined) {
                        setRowResultComparator(value);
                      }
                    }}
                  />
                  {rowResultComparator !== "any" && (
                    <>
                      <Box width={20}>
                        <NumberInput
                          aria-label="Row result percentage threshold"
                          size="sm"
                          width="100%"
                          min={0}
                          max={100}
                          placeholder="0"
                          value={rowResultThreshold}
                          onChange={setRowResultThreshold}
                        />
                      </Box>
                      <Text color="text.secondary">%</Text>
                    </>
                  )}
                </Row>

                {hasActiveFilters && (
                  <Tooltip message="Clear filters">
                    <IconButton
                      aria-label="Clear filters"
                      icon={CloseIcon}
                      size="sm"
                      variant="tertiary"
                      onClick={clearFilters}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </Row>
        )}

        <Table
          isPrivate
          columns={columns}
          data={runs}
          error={Boolean(error)}
          loading={isLoading || isPreviousData}
          placeholder={{
            title: hasActiveFilters ? "No matching runs" : "No sync runs",
            body: hasActiveFilters
              ? "Try adjusting your filters."
              : isJourneyTriggered
                ? "This sync will be triggered by your journey"
                : "Set a schedule or manually run the sync.",
            error: "Runs failed to load, please try again.",
            button:
              !isJourneyTriggered && !hasActiveFilters ? (
                <PermissionedButton
                  permission={{
                    v2: {
                      resource: "sync",
                      grant: "can_run",
                      id: sync?.id,
                    },
                  }}
                  isDisabled={overageLockout || Boolean(sync?.draft)}
                  tooltip={
                    sync?.draft
                      ? "Draft syncs cannot be run"
                      : overageLockout && overageText
                  }
                  onClick={onRun}
                  variant="primary"
                  icon={PlayIcon}
                >
                  Run sync
                </PermissionedButton>
              ) : undefined,
          }}
        />
        <BasicPagination
          showPageInput={Boolean(appSyncRunsPageInputEnabled)}
          pages={totalPages}
          disableNextPage={data?.sync_requests?.length !== PAGE_SIZE + 1}
          page={page}
          setPage={setPage}
        />
      </Column>
      <SyncRequestErrorModal
        isOpen={Boolean(runError)}
        onClose={() => setError(undefined)}
        syncRequestError={runError}
        sync={sync}
        syncStatus={runError?.syncStatus}
      />
    </>
  );
};
